import React, { useEffect } from "react"
import * as Sentry from "@sentry/react"
import { getStaticFeatures } from "../hooks/use-feature"
import { sendBeacon } from "../utils/send-beacon"
import {
  SENTRY_CONFIG,
  shouldEnableSentry,
  shouldFilterError,
  getBaseSentryConfig
} from "../lib/sentry-config"

function logFilteredSlateError(reason, errorValue, event) {
  const path = window.location.pathname.replace(/\d+/g, ":id")
  const user = window.app?.user

  sendBeacon(
    "/ui/log",
    JSON.stringify({
      event: "sentry_slate_error_filtered",
      filter_reason_string: reason,
      error_message_string: errorValue?.substring(0, 200),
      path_string: path,
      transaction_string: event.transaction || path,
      account_id: user?.account_id,
      user_id: user?.id
    })
  )
}

const shouldReportError =
  shouldEnableSentry() &&
  SENTRY_CONFIG.allowedHosts.includes(window.location.host)

if (shouldReportError) {
  Sentry.init({
    ...getBaseSentryConfig(),
    enabled: shouldReportError,
    integrations: [
      Sentry.browserTracingIntegration({
        // Only create spans for API requests, not static assets from CDN
        shouldCreateSpanForRequest: url => {
          // Exclude CDN domains (JS chunks, static assets) - from shared config
          if (SENTRY_CONFIG.cdnDomains.some(domain => url.includes(domain))) {
            return false
          }

          // Check for blocklisted paths (handles both relative and absolute URLs)
          if (
            SENTRY_CONFIG.blocklistedPaths?.some(path => {
              // For relative URLs: direct match
              if (url.startsWith(path)) return true
              // For absolute URLs: check if pathname starts with the path
              try {
                return new URL(url).pathname.startsWith(path)
              } catch {
                return false
              }
            })
          ) {
            return false
          }

          // Relative paths starting with "/" are requests to our own app
          if (url.startsWith("/")) {
            return true
          }

          // For full URLs, check if hostname ends with one of our app domains
          // This prevents false matches in query params like "?redirect=https://app.kit.com"
          try {
            const urlObj = new URL(url)
            return SENTRY_CONFIG.appDomains.some(
              domain =>
                urlObj.hostname === domain ||
                urlObj.hostname.endsWith("." + domain)
            )
          } catch {
            // If URL parsing fails, check if URL starts with domain or https://domain
            return SENTRY_CONFIG.appDomains.some(
              domain =>
                url.startsWith(domain) ||
                url.startsWith("https://" + domain) ||
                url.startsWith("http://" + domain)
            )
          }
        }
      }),
      Sentry.replayIntegration({
        maskAllText: SENTRY_CONFIG.replay.maskAllText,
        maskAllInputs: SENTRY_CONFIG.replay.maskAllInputs,
        blockAllMedia: SENTRY_CONFIG.replay.blockAllMedia
      })
    ],

    beforeSend(event, hint) {
      const error = hint.originalException
      const errorName = error?.name || event.exception?.values?.[0]?.type || ""
      const errorValue =
        error?.message || event.exception?.values?.[0]?.value || ""

      // Errors flagged with `bypassFilters` (e.g. set by React error boundaries
      // bc it caused a page crash) skip the noise filters entirely.
      const bypassFilters = Boolean(event?.contexts?.custom?.bypassFilters)

      if (!bypassFilters) {
        const filterReason = shouldFilterError(
          error,
          errorName,
          errorValue,
          event
        )
        if (filterReason) {
          if (filterReason === "slate" || filterReason === "yjs") {
            logFilteredSlateError(filterReason, errorValue, event)
          }
          return null
        }
      }

      // Track errors to analytics
      if (window.analytics) {
        const user = window.app?.user

        window.analytics.track("error", {
          path: window.location.pathname.replace(/\d+/g, ":id"),
          context: event.contexts,
          user_id: user?.id,
          account_id: user?.account_id,
          class_name: errorName
        })
      }

      // Attach static features
      const features = getStaticFeatures()
      if (features.length > 0) {
        event.contexts = event.contexts || {}
        event.contexts.features = features.reduce(
          (acc, feat) => ({ ...acc, [feat]: true }),
          {}
        )
      }

      return event
    }
  })
}

// Provide fallback ErrorBoundary when Sentry is not initialized
const ErrorBoundary = !shouldReportError
  ? ({ children }) => children
  : Sentry.ErrorBoundary

export function SentryErrorBoundary({ children }) {
  useEffect(() => {
    // Only set user data if Sentry is initialized
    if (!shouldReportError || !window.app) {
      return
    }
    const user = window.app.user

    // Set user identification in Sentry
    Sentry.setUser({
      id: user?.id,
      email: user?.email
    })

    // Add account metadata
    Sentry.setContext("account", {
      id: user?.account_id,
      created_at: user?.account_created_at
    })

    // Add admin context if impersonating
    if (user?.admin) {
      Sentry.setContext("admin", {
        id: window.app.admin_user?.id,
        email: window.app.admin_user?.email
      })
    }
  }, [])

  return <ErrorBoundary>{children}</ErrorBoundary>
}

window.SentryClient = Sentry
export { Sentry }
